import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
  Signal,
  WritableSignal,
} from '@angular/core';
import { IconType } from '@kinomap-dev/ng-ui-lib/core';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { TranslateModule } from '@ngx-translate/core';
import { MediaBlockService } from '../../services/media-block-service';
import { SynchroState, SynchroStatusType } from '../../types/synchroStatus.type';
import { SyncroInformation } from '../syncro-information/syncro-information';
import { UpperFirstLetterPipe } from '../../pipes/upper-first-letter-pipe';

export type SyncroStatusMode = 'inline' | 'fullscreen';

interface SynchroStateDisplay {
  status: SynchroStatusType;
  title: string;
  message: string;
  /** Message alternatif dans la modale fullscreen. */
  fullscreenMessage?: string;
  icon: IconType;
}

const STATE_DISPLAY: Record<Exclude<SynchroState, 'no-media'>, SynchroStateDisplay> = {
  'gps-too-short': {
    status: 'error',
    title: 'syncro.gps_too_short.title',
    message: 'syncro.gps_too_short.message',
    icon: 'timer',
  },
  'speed-warning': {
    status: 'speed-warning',
    title: 'syncro.speed_warning.title',
    message: 'syncro.speed_warning.message',
    icon: 'warning',
  },
  synced: {
    status: 'complete',
    title: 'syncro.confirmed.title',
    message: 'syncro.confirmed.message',
    icon: 'g_validate',
  },
  'likely-synced': {
    status: 'likely-synced',
    title: 'syncro.likely_synced.title',
    message: 'syncro.likely_synced.message',
    fullscreenMessage: 'syncro.likely_synced.message_fullscreen',
    icon: 'information',
  },
  'not-synced': {
    status: 'pending',
    title: 'syncro.not_verified.title',
    message: 'syncro.not_verified.message',
    icon: 'information',
  },
};

const FAQ_URL: string = 'https://support.kinomap.com/hc/fr';

/**
 * Point d'entrée unique pour tous les messages de synchro vidéo / GPS
 * (erreurs, succès, avertissements, astuces), dans le media-block (`inline`)
 * comme dans la modale (`fullscreen`).
 */
@Component({
  selector: 'app-syncro-status',
  imports: [TranslateModule, IconComponent, SyncroInformation, UpperFirstLetterPipe],
  templateUrl: './syncro-status.html',
  styleUrl: './syncro-status.scss',
})
export class SyncroStatus implements OnDestroy {
  protected readonly mediaBlockService: MediaBlockService = inject(MediaBlockService);

  readonly mode: InputSignal<SyncroStatusMode> = input<SyncroStatusMode>('inline');

  /** Mode inline : demande l'ouverture de l'éditeur de synchro (modale fullscreen). */
  readonly openEditor: OutputEmitterRef<void> = output<void>();

  private readonly syncMode: WritableSignal<boolean> = this.mediaBlockService.syncMode;

  protected readonly state: Signal<SynchroState> = this.mediaBlockService.synchroState;

  protected readonly display: Signal<SynchroStateDisplay | undefined> = computed(() => {
    const state: SynchroState = this.state();
    if (state === 'no-media') return undefined;
    // En mode synchro, « synchro non vérifiée » n'apporte rien et prend de la place sur les petits écrans.
    // TODO: à réafficher plus tard, peut-être uniquement après une tentative d'envoi du formulaire.
    if (state === 'not-synced' && this.mode() === 'fullscreen' && this.syncMode()) return undefined;
    // En cours de modification d'une synchro confirmée : le bouton « modifier » n'a plus lieu d'être.
    if (state === 'synced' && this.mode() === 'fullscreen' && this.syncMode()) return undefined;
    return STATE_DISPLAY[state];
  });

  protected readonly message: Signal<string | undefined> = computed(() => {
    const display: SynchroStateDisplay | undefined = this.display();
    if (!display) return undefined;
    return this.mode() === 'fullscreen' ? display.fullscreenMessage ?? display.message : display.message;
  });

  /** Synchro possible (y compris la modification d'une synchro déjà confirmée). */
  private readonly canSync: Signal<boolean> = computed(
    () => this.state() !== 'no-media' && this.state() !== 'gps-too-short',
  );

  /** Mode fullscreen : le bouton d'entrée en mode synchro est proposé tant que la synchro reste à faire. */
  protected readonly canEnterSyncMode: Signal<boolean> = computed(
    () =>
      this.mode() === 'fullscreen' &&
      this.canSync() &&
      !this.mediaBlockService.syncroComplete() &&
      !this.syncMode(),
  );

  /** Astuces pas à pas : uniquement dans la modale, en mode synchro. */
  protected readonly showTips: Signal<boolean> = computed(
    () => this.mode() === 'fullscreen' && this.mediaBlockService.syncModeActive(),
  );

  protected readonly tips: { text: string; advice: string; icon: IconType }[] = [
    { icon: 'video', text: 'syncro.tips.step_1.text', advice: 'syncro.tips.step_1.advice' },
    { icon: 'gps_stroke', text: 'syncro.tips.step_2.text', advice: 'syncro.tips.step_2.advice' },
    { icon: 'g_validate', text: 'syncro.tips.step_3.text', advice: 'syncro.tips.step_3.advice' },
  ];

  /** Le champ syncroTime est requis : on le signale en mode inline après soumission. */
  protected hasRequiredError(): boolean {
    if (this.mode() !== 'inline') return false;
    const control = this.mediaBlockService.mediaForm?.get('syncroTime');
    return !!control?.invalid && !!control?.touched;
  }

  protected openFaq(): void {
    window.open(FAQ_URL, '_blank', 'noopener');
  }

  /** Passe en mode synchro ; en inline, ouvre aussi la modale fullscreen. */
  protected verify(): void {
    this.syncMode.set(true);
    if (this.mode() === 'inline') this.openEditor.emit();
  }

  /** Repasse en mode synchro en conservant la synchro confirmée : elle reste en place tant qu'une nouvelle n'est pas validée. */
  protected edit(): void {
    this.syncMode.set(true);
    if (this.mode() === 'inline') this.openEditor.emit();
  }

  /** À la fermeture de la modale, on sort du mode synchro. */
  ngOnDestroy(): void {
    if (this.mode() === 'fullscreen') this.syncMode.set(false);
  }

  /** Quitte le mode synchro sans valider : le repère posé sur la carte est retiré. */
  protected cancelSyncMode(): void {
    this.syncMode.set(false);
    this.mediaBlockService.syncMarkerCoordinate.set(undefined);
  }
}
